<template>
  <div>
    <div class="fake-input" @click="openDialog">
      <span class="input-value">{{ displayValue }}</span>
    </div>

    <div class="barcode-number-block">
      <span>
        {{ $t('number_of_scanned_items') }}
      </span>
      <span class="label-box"> {{ listValue.length }}</span>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :width="'100%'"
      :fullscreen="true"
      :destroy-on-close="true"
      :lock-scroll="true"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <template #title>
        <div class="flex justify-center items-center">
          <div class="label-round" style="display: block;">{{ $t('list_scanned_items') }}</div>
        </div>
      </template>
      <div class="edit-view" v-loading.fullscreen="loading">
        <div class="overflow-y-auto border-2 border-solid border-gray-300 rounded-lg p-4 list-container">
          <div v-if="listValue.length > 0">
            <div
              v-for="(item, index) in listValue"
              :key="index"
              class="barcode-item-wrapper"
              :class="{ dragging: isDragging && dragIndex === index, 'drag-over': dragOverIndex === index }"
              :draggable="isDragging && dragIndex === index"
              @dragstart="handleDragStart($event, index)"
              @dragover="handleDragOver($event, index)"
              @drop="handleDrop($event, index)"
              @dragend="handleDragEnd"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              :style="isDragging && dragIndex === index ? dragStyle : {}"
            >
              <div class="barcode-item">
                <!-- Drag Handle -->
                <div
                  class="drag-handle"
                  @mousedown="startDrag($event, index)"
                  @touchstart="startDrag($event, index)"
                  @touchmove.prevent
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path
                      d="M2 4h12a1 1 0 0 1 0 2H2a1 1 0 0 1 0-2zm0 3h12a1 1 0 0 1 0 2H2a1 1 0 0 1 0-2zm0 3h12a1 1 0 0 1 0 2H2a1 1 0 0 1 0-2z"
                    />
                  </svg>
                </div>

                <span class="item-text">{{ item.value }}</span>

                <div class="item-actions">
                  <span class="item-index">#{{ index + 1 }}</span>
                  <el-button type="danger" size="small" @click="removeItem(index)" class="delete-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                      <path
                        d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12zm-7 4v6m4-6v6"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            {{ $t('no_items_to_display') }}
          </div>
        </div>

        <!-- Manual input section when not in scan mode -->
        <div v-if="!isScan" class="manual-input-section">
          <div class="input-row">
            <el-input
              v-model="inputValue"
              :placeholder="$t('enter_item_manually')"
              class="manual-input"
              @keydown.enter="addItem"
            />
            <el-button
              type="primary"
              @click="addItem"
              class="add-btn"
              :disabled="!inputValue.trim() || wouldExceedMaxLength"
            >
              {{ $t('ok') }}
            </el-button>
          </div>
        </div>

        <div v-else class="scan-mode-buttons">
          <el-button type="primary" @click="openScanDialog()" class="scan-add-btn" size="large">
            <div class="button-content">
              <span class="button-text">
                {{ $t('scan_items') }}
              </span>
            </div>
          </el-button>
        </div>

        <div class="barcode-number-block">
          <span>
            {{ $t('number_of_scanned_items') }}
          </span>
          <span class="label-box">
            {{ listValue.length }}
          </span>
        </div>

        <div class="dialog-actions">
          <el-button @click="cancelChanges" class="cancel-btn">
            {{ $t('cancel') }}
          </el-button>
          <el-button type="primary" @click="confirmChanges" class="confirm-btn" :disabled="hasValidationError">
            {{ $t('confirm') }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Scan Mode Dialog -->
    <ListStringScanComponent
      v-if="scanDialogVisible"
      v-model="scanDialogVisible"
      :scanned-values="localValue"
      :min-value="minLength"
      :max-value="maxLength"
      @update:confirm="onScanConfirm"
    />
  </div>
</template>

<script lang="ts">
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import ListStringScanComponent from '@/components/ListStringScanComponent.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { openMessage } from '@/utils/helpers'
import { IListStringItem } from 'smartbarcode-web-core/src/utils/types'
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeSymbologiesSelection,
    SelectionCameraPopup,
    IconSwitch,
    ScanArea,
    ListStringScanComponent,
  },
  emits: ['update:modelValue', 'change'],
  inheritAttrs: false,
  name: 'InputListString',
})
export default class InputListString extends Vue {
  @Prop({ type: Array, default: () => [] }) modelValue?: IListStringItem[]
  @Prop({ type: Boolean, default: false }) isScan?: boolean
  @Prop({ type: String, default: '' }) fieldSchema?: string
  @Prop({ type: Number }) readonly minLength?: number
  @Prop({ type: Number }) readonly maxLength?: number
  @Prop({ type: String }) readonly fieldName?: string
  @Prop({ type: String }) readonly error?: string

  dialogVisible = false
  loading = false
  localValue: IListStringItem[] = []

  // Drag and drop state
  isDragging = false
  dragIndex = -1
  dragOverIndex = -1
  inputValue = ''

  // Touch drag state
  touchStartY = 0
  touchCurrentY = 0
  dragStyle = {}

  // Scan mode state
  scanDialogVisible = false
  scanLoading = false
  manualScanValue = ''

  // Camera and barcode scanner state
  isReadyToScan = true
  scannedDialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IListStringItem[] = []

  mounted() {
    this.localValue = [...(this.modelValue || [])]
  }

  get listValue(): IListStringItem[] {
    return this.localValue || []
  }

  get displayValue(): string {
    if (!this.listValue || this.listValue.length === 0) {
      return this.$t('add')
    }
    return this.$t('view')
  }

  get validationError(): string {
    const length = this.listValue.length

    if (this.error) {
      return this.error
    }

    if (this.minLength !== undefined && length < this.minLength) {
      return this.$t('minimum_items_required', { minLength: this.minLength })
    }

    if (this.maxLength !== undefined && length > this.maxLength) {
      return this.$t('maximum_items_required', { maxLength: this.maxLength })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  get wouldExceedMaxLength(): boolean {
    return this.maxLength !== undefined && this.localValue.length >= this.maxLength
  }

  @Emit('update:modelValue')
  updateModelValue(newList: IListStringItem[]) {
    return newList
  }

  @Emit('change')
  emitChange(newList: IListStringItem[]) {
    return newList
  }

  addItem(e: Event) {
    e.preventDefault()
    if (this.inputValue.trim() && !this.wouldExceedMaxLength) {
      const newItem: IListStringItem = {
        value: this.inputValue.trim(),
        createdDateTime: new Date().toISOString(),
      }
      this.localValue = [...this.localValue, newItem]
      this.inputValue = ''
    }
  }

  removeItem(index: number) {
    this.localValue = this.localValue.filter((_, i) => i !== index)
  }

  // Touch-friendly move functions
  moveItem(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= this.localValue.length) return

    const newList = [...this.localValue]
    const [movedItem] = newList.splice(fromIndex, 1)
    newList.splice(toIndex, 0, movedItem)
    this.localValue = newList
  }

  // Drag and drop functions
  startDrag(event: MouseEvent | TouchEvent, index: number) {
    this.isDragging = true
    this.dragIndex = index

    if (event.type === 'touchstart') {
      const touch = (event as TouchEvent).touches[0]
      this.touchStartY = touch.clientY
      this.touchCurrentY = touch.clientY

      // Prevent scrolling while dragging
      document.body.style.overflow = 'hidden'
    }
  }

  handleDragStart(event: DragEvent, index: number) {
    if (!this.isDragging || this.dragIndex !== index) {
      event.preventDefault()
      return
    }

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', index.toString())
    }
  }

  handleDragOver(event: DragEvent, index?: number) {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    if (index !== undefined) {
      this.dragOverIndex = index
    }
  }

  handleDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault()

    const draggedIndex = parseInt(event.dataTransfer?.getData('text/plain') || '-1')

    if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
      this.moveItem(draggedIndex, dropIndex)
    }

    this.handleDragEnd()
  }

  handleDragEnd() {
    this.isDragging = false
    this.dragIndex = -1
    this.dragOverIndex = -1
    this.dragStyle = {}

    // Re-enable scrolling
    document.body.style.overflow = ''
  }

  handleTouchMove(event: TouchEvent) {
    if (!this.isDragging) return

    event.preventDefault()
    const touch = event.touches[0]
    this.touchCurrentY = touch.clientY

    const deltaY = this.touchCurrentY - this.touchStartY

    // Update drag style for visual feedback
    this.dragStyle = {
      transform: `translateY(${deltaY}px)`,
      zIndex: 1000,
      opacity: 0.8,
    }

    // Find the element we're hovering over
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY)
    const targetWrapper = elements.find((el) => el.classList.contains('barcode-item-wrapper'))

    if (targetWrapper) {
      const allWrappers = Array.from(document.querySelectorAll('.barcode-item-wrapper'))
      const targetIndex = allWrappers.indexOf(targetWrapper)
      if (targetIndex !== -1 && targetIndex !== this.dragIndex) {
        this.dragOverIndex = targetIndex
      }
    }
  }

  handleTouchEnd(event: TouchEvent) {
    if (!this.isDragging) return

    event.preventDefault()

    // If we have a valid drop target, perform the move
    if (this.dragOverIndex !== -1 && this.dragOverIndex !== this.dragIndex) {
      this.moveItem(this.dragIndex, this.dragOverIndex)
    }

    this.handleDragEnd()
  }

  confirmChanges() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
    this.dialogVisible = false
  }

  async cancelChanges() {
    // Check if there are unsaved changes
    const hasChanges = JSON.stringify(this.localValue) !== JSON.stringify(this.modelValue || [])

    if (hasChanges) {
      const originalLength = this.modelValue?.length || 0
      const currentLength = this.localValue.length
      const changesSummary =
        originalLength !== currentLength
          ? ` (${originalLength} → ${currentLength} items)`
          : ` (${currentLength} items modified)`

      try {
        await this.$confirm(
          this.$t('unsaved_changes', { changes: changesSummary }),
          this.$t('title_unsaved_changes', { changes: changesSummary }),
          {
            confirmButtonText: this.$t('discard_changes'),
            cancelButtonText: this.$t('keep_editing'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
      } catch (error) {
        return
      }
    }

    // Reset local value to original modelValue
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = false
  }

  openDialog() {
    // Reset local value to current modelValue when opening dialog
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = true
  }

  // Scan mode methods
  openScanDialog() {
    this.scannedBarcodeList = []
    this.manualScanValue = ''
    this.scanDialogVisible = true
  }

  onScanConfirm(scannedValue: string, scanMode: 'add' | 'remove') {
    if (scannedValue.trim()) {
      if (scanMode === 'add') {
        if (this.wouldExceedMaxLength) {
          openMessage(this.$t('maximum_items_reached', { maxItems: this.maxLength }), 'warning')
          return
        }
        // Add scanned item to the list if it doesn't already exist
        const existingItem = this.localValue.find((item) => item.value === scannedValue.trim())
        if (!existingItem) {
          const newItem: IListStringItem = {
            value: scannedValue.trim(),
            createdDateTime: new Date().toISOString(),
          }
          this.localValue = [...this.localValue, newItem]
          openMessage(this.$t('item_detected'), 'success')
        } else {
          openMessage(this.$t('item_already_scanned'), 'info')
        }
      } else {
        // Remove scanned item from the list
        const index = this.localValue.findIndex((item) => item.value === scannedValue.trim())
        if (index > -1) {
          openMessage(this.$t('item_deleted'), 'success')
          this.localValue.splice(index, 1)
        }
      }
    }
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.scannedDialogVisible = true
  }

  onCloseScannedDialog() {
    this.scannedDialogVisible = false
    this.checkScannedBarcode = false
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

.custom-barcode-number-block {
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: $dark-grey;
  color: #fff;
  border: none;
}

.custom-barcode-number-block:active {
  transform: translateY(1px);
}

.input-label {
  cursor: pointer;
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  transition: color 0.2s ease;
}

.fake-input {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid $dark-grey;
  border-radius: 6px;
  background-color: $dark-grey;
  transition: all 0.2s ease;
  min-height: 44px;
  box-sizing: border-box;
  color: white;
}

.fake-input:focus-within {
  border-color: #409eff;
  outline: none;
}

.input-value {
  flex: 1;
  color: white;
  font-size: 14px;
  text-align: center;
}

.fake-input .input-value:empty::before {
  content: 'Click to view items';
  color: #999;
}

.list-items {
  max-height: 300px;
  overflow-y: auto;
}

.list-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.list-item:last-child {
  border-bottom: none;
}

.list-container {
  height: 40vh;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.barcode-item-wrapper {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;

  &[draggable='true'] {
    cursor: move;
    opacity: 0.8;
  }

  &.dragging {
    z-index: 1000;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    transform: scale(1.02);
  }

  &.drag-over {
    background-color: #e3f2fd;
    border: 2px dashed #2196f3;
    margin: 4px 0;
  }

  // Prevent text selection during drag
  &.dragging * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
}

.barcode-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 8px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;

  .drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 8px;
    cursor: grab;
    color: #666;
    border-radius: 6px;
    transition: all 0.2s ease;
    touch-action: none; // Prevent scrolling when touching the handle

    &:hover {
      background-color: #e9ecef;
      color: #333;
    }

    &:active {
      cursor: grabbing;
      background-color: #dee2e6;
    }

    // Mobile responsive - larger touch target
    @media (max-width: 768px) {
      width: 40px;
      height: 40px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    svg {
      width: 16px;
      height: 16px;
      pointer-events: none; // Prevent svg from interfering with touch events
    }
  }

  .item-text {
    flex: 1;
    font-weight: 500;
    color: #333;
    word-break: break-word;
    margin-left: 8px;
  }

  .item-actions {
    margin-left: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .item-index {
    font-size: 12px;
    color: #666;
    background-color: #e9ecef;
    padding: 2px 8px;
    border-radius: 12px;
  }

  .delete-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50% !important;
    font-size: 16px;
    font-weight: bold;
    color: white !important;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    min-width: 32px !important;
    border: none !important;

    // Mobile responsive - larger touch target
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      min-width: 36px !important;
    }

    &:hover {
      opacity: 0.8;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.3);
    }
  }

  .move-btn {
    min-width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    // Mobile responsive - larger buttons on mobile
    @media (max-width: 768px) {
      min-width: 36px;
      height: 36px;
      font-size: 14px;
    }
  }
}

.manual-input-section {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #f8f9fa;

  .input-row {
    display: flex;
    gap: 12px;
    align-items: center;

    .manual-input {
      flex: 1;
    }

    .add-btn {
      min-width: 60px;
      height: 40px;
    }
  }
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  .cancel-btn,
  .confirm-btn {
    flex: 1;
    height: 44px;
    font-weight: 500;
  }

  .cancel-btn {
    background-color: white;
    border-color: #6c757d;
    color: #6c757d;
  }

  .confirm-btn {
    background-color: $dark-grey;
    border-color: $dark-grey;
    color: white;
  }
}

.empty-state {
  text-align: center;
  color: #999;
}

.dialog-footer {
  text-align: right;
}

.edit-view {
  padding: 0 16px 16px 16px;
}

.error-msg {
  color: #f56c6c;
  font-size: 14px;
  line-height: 1.2;
  margin-top: 4px;
  display: block;
}

.display-in-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

::v-deep .el-dialog__header {
  display: block !important;
}

::v-deep .el-dialog__body {
  height: 100%;
  padding: 0px;
  // margin: 0 20px 30px 20px;
}

// Scan Mode Styles
.scan-mode-buttons {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;

  .scan-add-btn,
  .scan-remove-btn {
    flex: 1;
    max-width: 150px;
    height: 25px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;
    margin: 0;
    position: relative;
    padding: 0px 10px;

    .button-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      position: relative;

      .button-icon {
        position: absolute;
        left: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 768px) {
          left: 16px;
        }
      }

      .button-text {
        flex: 1;
        text-align: center;
        font-size: 14px;

        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
  }
}

.scan-view {
  padding: 0 16px 16px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scanner-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.scanner-frame {
  position: relative;
  width: 280px;
  height: 280px;
  border: 2px dashed #409eff;
  border-radius: 12px;
  background: linear-gradient(45deg, rgba(64, 158, 255, 0.05) 0%, rgba(64, 158, 255, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 220px;
    height: 220px;
  }
}

.scanner-line {
  position: absolute;
  width: 80%;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #409eff 50%, transparent 100%);
  animation: scan-line 2s linear infinite;
}

@keyframes scan-line {
  0% {
    top: 20px;
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    top: calc(100% - 20px);
    opacity: 0;
  }
}

.scanner-corners {
  position: absolute;
  width: 100%;
  height: 100%;

  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid #409eff;

    &.top-left {
      top: -2px;
      left: -2px;
      border-right: none;
      border-bottom: none;
    }

    &.top-right {
      top: -2px;
      right: -2px;
      border-left: none;
      border-bottom: none;
    }

    &.bottom-left {
      bottom: -2px;
      left: -2px;
      border-right: none;
      border-top: none;
    }

    &.bottom-right {
      bottom: -2px;
      right: -2px;
      border-left: none;
      border-top: none;
    }
  }
}

.scanner-info {
  text-align: center;

  .mode-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 12px;
    display: inline-flex;

    &.add {
      background-color: rgba(64, 158, 255, 0.1);
      color: #409eff;
    }

    &.remove {
      background-color: rgba(245, 108, 108, 0.1);
      color: #f56c6c;
    }
  }

  .scan-instructions {
    color: #666;
    font-size: 14px;
    margin: 0;
  }
}

.scanned-preview {
  margin: 16px 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h4 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 14px;
  }

  .preview-list {
    max-height: 120px;
    overflow-y: auto;

    .preview-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background-color: white;
      border-radius: 6px;
      margin-bottom: 8px;
      border: 1px solid #e9ecef;

      .item-value {
        flex: 1;
        font-size: 14px;
        color: #333;
      }

      .remove-preview-btn {
        color: #f56c6c;
        font-size: 18px;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: rgba(245, 108, 108, 0.1);
        }
      }
    }
  }
}

.manual-scan-input {
  margin: 16px 0;
  padding: 16px;
  background-color: #f0f7ff;
  border-radius: 8px;
  border: 1px solid #b3d8ff;

  .input-row {
    display: flex;
    gap: 12px;
    align-items: center;

    .manual-input {
      flex: 1;
    }

    .simulate-btn {
      min-width: 100px;
      height: 40px;
    }
  }
}

// Camera scanner styles (from PairScan component)

.step-label {
  margin-bottom: 25px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  padding: 8px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
}

.scan-dialog-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  .cancel-btn,
  .apply-btn {
    flex: 1;
    height: 44px;
    font-weight: 500;
  }

  .cancel-btn {
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
  }

  .apply-btn {
    background-color: #409eff;
    border-color: #409eff;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
